import React, { useState, useRef, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import styles from './Reviews.module.css';
import SEO from '../components/SEO';

const Reviews = () => {
  const [expandedReviews, setExpandedReviews] = useState({});
  const textRefs = useRef({});

  const setTextRef = useCallback((id) => (el) => {
    textRefs.current[id] = el;
  }, []);

  const reviews = [
    {
      id: 1,
      name: 'Frank Throop',
      rating: 5,
      date: 'March 2026',
      text: 'After having a stroke and being life flighted to Eisenhower Medical Center, I was admitted into Rehabilitation Hospital of Southern California where I was blessed to start receiving services from this organization. The entire staff from the director (Nick C) to the therapists that came to my house were nothing but compassionate as well as professional. If anyone is considering receiving services from All For You Home Care, I assure you, you won\'t be disappointed.',
      initials: 'FT'
    },
    {
      id: 2,
      name: 'Neysa Wells',
      rating: 5,
      date: 'February 2026',
      text: 'All For You Home Health Care has been an amazing home health service for my father who suffers from traumatic brain injury, Alzheimer\'s, and dementia. Each one of the team members are so patient, kind and courteous. Nicholas, the owner, took the time during the Christmas holidays to come out, meet my dad personally and brought candy to welcome us to the All For You Home Health family. All For You Home Health could not have come into my life any sooner - God knew we needed them.',
      initials: 'NW'
    },
    {
      id: 3,
      name: 'Don Nelson',
      rating: 5,
      date: 'February 2026',
      text: 'As a patient advocate for more than 30 years, I have only high praise for Nicholas Churchian and his staff of caregivers. The team has been extremely courteous, from the home care aid to the physical therapist, RN and the occupational therapist. Marianne six weeks ago at 91 years old was ready to give up. With the help and encouragement of everyone on the team, Marianne has thrived and is back on her feet again. Without the help of All For You Home Care, the outcome would have been very different.',
      initials: 'DN'
    },
    {
      id: 4,
      name: 'Aleksandr Kirakosyan',
      rating: 5,
      date: 'March 2026',
      text: 'From the very first phone call, All For You Home Health Care made us feel like family. The caregivers are attentive, skilled, and truly go above and beyond every single day. Communication with the team is seamless - they always keep us updated and address any concerns right away. I would highly recommend them to anyone looking for reliable and compassionate home health care.',
      initials: 'AK'
    },
    {
      id: 5,
      name: 'Carolyn Hanley',
      rating: 5,
      date: 'March 2026',
      text: 'I cannot say enough wonderful things about All For You Home Health Care. The level of professionalism and genuine kindness from every staff member has been remarkable. They created a personalized care plan that addressed all of our needs and consistently followed through. It is clear that this team truly loves what they do - the compassion and dedication they bring into our home every visit has made all the difference for our family.',
      initials: 'CH'
    }
  ];

  const handleLeaveReview = () => {
    window.open('https://www.google.com/maps/place/All+For+You+Home+Health+Care/data=!4m2!3m1!1s0x0:0x25b70e7d9342d39f?sa=X&ved=1t:2428&ictx=111', '_blank');
  };

  const toggleExpand = (id) => {
    const el = textRefs.current[id];
    if (el) {
      if (!expandedReviews[id]) {
        el.style.maxHeight = el.scrollHeight + 'px';
      } else {
        el.style.maxHeight = null;
      }
    }
    setExpandedReviews(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <svg
        key={index}
        className={index < rating ? styles.starFilled : styles.starEmpty}
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" />
      </svg>
    ));
  };

  return (
    <div className={styles.reviewsPage}>
      <SEO
        title="Reviews & Testimonials"
        description="Read what our clients say about All For You Home Health Care. Real reviews and testimonials from families we've helped throughout Southern California."
        keywords="reviews, testimonials, client feedback, home health care reviews, patient testimonials, family reviews"
        canonicalUrl="https://allforyouhc.com/reviews"
      />
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroContainer}>
          <p className={styles.heroTag}>CLIENT TESTIMONIALS</p>
          <h1 className={styles.heroTitle}>
            Trusted by Families<br />Across Southern California
          </h1>
          <p className={styles.heroDescription}>
            Read what our clients and their families have to say about the care and support
            they've received. Every review represents a family we've had the honor to serve.
          </p>
        </div>
      </section>

      {/* Reviews Slider Section */}
      <section className={styles.reviewsSection}>
        <div className={styles.container}>
          <div className={styles.sliderWrapper}>
            <Swiper
              modules={[Navigation, Autoplay]}
              spaceBetween={24}
              slidesPerView={2.4}
              loop={true}
              navigation={{
                nextEl: `.${styles.navButtonNext}`,
                prevEl: `.${styles.navButtonPrev}`,
              }}
              autoplay={{
                delay: 5000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              breakpoints={{
                0: {
                  slidesPerView: 1.1,
                  spaceBetween: 16,
                },
                480: {
                  slidesPerView: 1.2,
                  spaceBetween: 16,
                },
                768: {
                  slidesPerView: 1.8,
                  spaceBetween: 20,
                },
                992: {
                  slidesPerView: 2.4,
                  spaceBetween: 24,
                },
              }}
              className={styles.swiperContainer}
            >
              {reviews.map((review) => (
                <SwiperSlide key={review.id}>
                  <div className={styles.reviewCard}>
                    <div className={styles.stars}>
                      {renderStars(review.rating)}
                    </div>
                    <p
                      ref={setTextRef(review.id)}
                      className={`${styles.reviewText} ${expandedReviews[review.id] ? styles.reviewTextExpanded : ''}`}
                    >{review.text}</p>
                    <button className={styles.readMoreButton} onClick={() => toggleExpand(review.id)}>
                      {expandedReviews[review.id] ? 'Read less' : 'Read more'}
                    </button>
                    <div className={styles.reviewFooter}>
                      <div className={styles.avatar}>{review.initials}</div>
                      <div className={styles.reviewerInfo}>
                        <h3 className={styles.reviewerName}>{review.name}</h3>
                        <p className={styles.reviewDate}>{review.date}</p>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            <button
              className={styles.navButton + ' ' + styles.navButtonPrev}
              aria-label="Previous reviews"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button
              className={styles.navButton + ' ' + styles.navButtonNext}
              aria-label="Next reviews"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>Share Your Experience</h2>
            <p className={styles.ctaText}>
              Your feedback helps us improve and helps other families make informed decisions
              about their home health care needs.
            </p>
            <button className={styles.ctaButton} onClick={handleLeaveReview}>
              Leave a Review on Google
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
